import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  IconButton,
  Stack,
} from '@mui/material';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { useId } from 'react';

import { Text, api } from 'common';

import {
  CUSTOMER_MESSAGE_HELPER_TEXT,
  CUSTOMER_MESSAGE_LABEL,
  DESCRIPTION_HELPER_TEXT,
  DESCRIPTION_LABEL,
  DESTINATION_HELPER_TEXT,
  DESTINATION_MESSAGE_HELPER_TEXT,
  DESTINATION_MESSAGE_LABEL,
  DESTINATION_TYPE_LABEL,
  EXTENSION_HELPER_TEXT,
  EXTENSION_LABEL,
  EXTENSION_PLACEHOLDER,
  NUMBER_HELPER_TEXT,
  NUMBER_LABEL,
  NUMBER_PLACEHOLDER,
  SIP_URI_HELPER_TEXT,
  SIP_URI_LABEL,
  SIP_URI_PLACEHOLDER,
  SUMMARY_MESSAGES_SYSTEM_PROMPT_HELPER_TEXT,
  SUMMARY_MESSAGES_SYSTEM_PROMPT_LABEL,
  TOOL_DESCRIPTION_HELPER_TEXT,
  TOOL_DESCRIPTION_LABEL,
  TOOL_DESCRIPTION_PLACEHOLDER,
  TRANSFER_MODE_HELPER_TEXT,
  TRANSFER_MODE_LABEL,
} from '../../../../../../../VoiceAssistants/TransferTab/constants';
import { useReactFlowViewContext } from '../../../../context/context';
import {
  BlockValueTemplateField,
  BlockValueTemplateFieldArea,
} from '../../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockSelectField } from '../../../ConfigFields/SelectField';
import { BlockTextField } from '../../../ConfigFields/TextField';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField';

const {
  VoiceCallTransferDestinationTypeToVoiceCallTransferCallModes,
  VoiceCallTransferCallModeToDisplayNameAndDescription,
  VoiceCallTransferCallToolDestinationType,
  VoiceCallTransferCallMode,
} = api.FlowDefinition;

function getDefaultDestination(): api.FlowDefinition.VoiceCallTransferCallToolDestination {
  return {
    type: VoiceCallTransferCallToolDestinationType.Number,
    number: '',
    description: '',
    transferPlan: {
      mode: VoiceCallTransferCallMode.BlindTransfer,
    },
    callerId: undefined,
    message: undefined,
  };
}

export function CallTransferFormFields(props: {
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
}) {
  const { expanded, onChange } = props;
  const id = useId();
  const { value: transferCallTool, setValue } =
    useSelectedNodeField<api.FlowDefinition.VoiceCallBlock['config']['transferCallTool']>(
      'config.transferCallTool'
    );
  const { isEditable } = useReactFlowViewContext();

  const destinations = transferCallTool?.destinations || [];

  return (
    <Accordion variant="elevation" expanded={expanded} onChange={onChange}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} id={id} aria-controls="tools-content">
        <Text variant="heading5">Voice call transfer</Text>
      </AccordionSummary>
      <AccordionDetails sx={{ gap: 2 }}>
        <Stack gap={2}>
          <BlockTextField
            fieldName={`config.toolToToolDescriptionMap.${api.AssistantToolDefinition.AssistantToolType.TransferCall}`}
            label={TOOL_DESCRIPTION_LABEL}
            helperText={TOOL_DESCRIPTION_HELPER_TEXT}
            placeholder={TOOL_DESCRIPTION_PLACEHOLDER}
            multiline
            minRows={2}
          />
          <Text variant="heading6">Transfer call tool destinations</Text>
          <Stack gap={2}>
            {destinations.map((destination, index) => {
              const isNumberDestination =
                destination.type === VoiceCallTransferCallToolDestinationType.Number;
              return (
                <Stack key={index} spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Text variant="heading6">Transfer destination {index + 1}</Text>
                    <IconButton
                      aria-label="Delete Field"
                      size="medium"
                      onClick={() => {
                        setValue({ destinations: destinations.filter((_, i) => i !== index) });
                      }}
                      disabled={!isEditable}
                    >
                      <TrashIcon size={16} />
                    </IconButton>
                  </Stack>
                  <BlockSelectField
                    fieldName={`config.transferCallTool.destinations.${index}.type`}
                    label={DESTINATION_TYPE_LABEL}
                    options={Object.values(VoiceCallTransferCallToolDestinationType).map(
                      (type) => ({
                        value: type,
                        label:
                          type === VoiceCallTransferCallToolDestinationType.Number
                            ? 'Phone Number'
                            : 'SIP URI',
                      })
                    )}
                    helperText={DESTINATION_HELPER_TEXT}
                    required
                  />
                  <BlockValueTemplateField
                    fieldName={`config.transferCallTool.destinations.${index}.${isNumberDestination ? 'number' : 'sipUri'}`}
                    label={isNumberDestination ? NUMBER_LABEL : SIP_URI_LABEL}
                    helperText={isNumberDestination ? NUMBER_HELPER_TEXT : SIP_URI_HELPER_TEXT}
                    placeholder={
                      isNumberDestination
                        ? `${NUMBER_PLACEHOLDER} or curly braces for dynamic number`
                        : `${SIP_URI_PLACEHOLDER} or curly braces for dynamic sip uri`
                    }
                    required
                  />
                  {isNumberDestination && (
                    <BlockValueTemplateField
                      fieldName={`config.transferCallTool.destinations.${index}.extension`}
                      label={EXTENSION_LABEL}
                      helperText={EXTENSION_HELPER_TEXT}
                      placeholder={EXTENSION_PLACEHOLDER}
                    />
                  )}
                  <BlockSelectField
                    fieldName={`config.transferCallTool.destinations.${index}.transferPlan.mode`}
                    label={TRANSFER_MODE_LABEL}
                    options={VoiceCallTransferDestinationTypeToVoiceCallTransferCallModes[
                      destination.type
                    ].map((mode) => ({
                      value: mode,
                      label: VoiceCallTransferCallModeToDisplayNameAndDescription[mode].displayName,
                      title: VoiceCallTransferCallModeToDisplayNameAndDescription[mode].description,
                    }))}
                    helperText={TRANSFER_MODE_HELPER_TEXT}
                    required
                  />
                  <BlockValueTemplateFieldArea
                    fieldName={`config.transferCallTool.destinations.${index}.description`}
                    label={DESCRIPTION_LABEL}
                    helperText={DESCRIPTION_HELPER_TEXT}
                    required
                  />
                  {destination.transferPlan?.mode !==
                    VoiceCallTransferCallMode.TransferWithDtmfCallToken && (
                    <>
                      <BlockValueTemplateFieldArea
                        fieldName={`config.transferCallTool.destinations.${index}.message`}
                        label={CUSTOMER_MESSAGE_LABEL}
                        helperText={CUSTOMER_MESSAGE_HELPER_TEXT}
                        required
                      />
                      {getFieldsForMode(destination.transferPlan?.mode, index).map((field) => (
                        <BlockValueTemplateFieldArea
                          key={field.fieldName}
                          fieldName={field.fieldName}
                          label={field.label}
                          helperText={field.helperText}
                          required
                        />
                      ))}
                    </>
                  )}
                </Stack>
              );
            })}
          </Stack>
          <Button
            variant="contained"
            onClick={() => {
              setValue({ destinations: [...destinations, getDefaultDestination()] });
            }}
            sx={{ alignSelf: 'flex-start', display: 'flex', gap: 1 }}
            disabled={!isEditable}
          >
            <PlusIcon size={12} />
            Add Destination
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function getFieldsForMode(
  mode: api.FlowDefinition.VoiceCallTransferCallMode | undefined,
  index: number
): { fieldName: string; label: string; helperText?: string }[] {
  switch (mode) {
    case VoiceCallTransferCallMode.BlindTransfer:
      return [];
    case VoiceCallTransferCallMode.WarmTransferSayMessage:
    case VoiceCallTransferCallMode.WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage:
      return [
        {
          fieldName: `config.transferCallTool.destinations.${index}.transferPlan.message`,
          label: DESTINATION_MESSAGE_LABEL,
          helperText: DESTINATION_MESSAGE_HELPER_TEXT,
        },
      ];
    case VoiceCallTransferCallMode.BlindTransferAddSummaryToSipHeader:
    case VoiceCallTransferCallMode.WarmTransferSaySummary:
    case VoiceCallTransferCallMode.WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary:
      return [
        {
          fieldName: `config.transferCallTool.destinations.${index}.transferPlan.summaryPlan.messages.0.content`,
          label: SUMMARY_MESSAGES_SYSTEM_PROMPT_LABEL,
          helperText: SUMMARY_MESSAGES_SYSTEM_PROMPT_HELPER_TEXT,
        },
      ];
    // These modes are not supported yet
    case VoiceCallTransferCallMode.WarmTransferTwiml:
    case VoiceCallTransferCallMode.WarmTransferExperimental:
    default:
      return [];
  }
}
